/**
 * SolicitudesActualizacionPage — Bandeja de cambios enviados por las agencias
 * vía portal público. Diff visual antes/después + aprobar/rechazar.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, Button, Chip, Alert, IconButton,
  TextField, Divider, CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import EditIcon from '@mui/icons-material/Edit';
import SlideOver from '../common/SlideOver';
import EmptyState from '../common/EmptyState';
import useToast from '../common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const CAMPOS_LABEL = {
  nombre: 'Razón social',
  tipo: 'Tipo',
  nit_cedula: 'NIT / Cédula',
  direccion: 'Dirección',
  pais: 'País',
  ciudad: 'Ciudad',
  telefono_general: 'Teléfono general',
  correo: 'Correo corporativo',
};
const CAMPO_CONTACTO_LABEL = {
  nombre: 'Nombre',
  numero_whatsapp: 'WhatsApp',
  correo: 'Correo',
  cargo: 'Cargo',
  identificacion: 'Identificación',
  es_principal: 'Principal',
};

const ESTADO_CFG = {
  pendiente:        { color: '#f59e0b', bg: '#fef3c7', label: 'Pendiente' },
  aprobada:         { color: '#16a34a', bg: '#dcfce7', label: 'Aprobada' },
  aprobada_parcial: { color: '#0284c7', bg: '#dbeafe', label: 'Aprobada parcial' },
  rechazada:        { color: '#dc2626', bg: '#fee2e2', label: 'Rechazada' },
};

export default function SolicitudesActualizacionPage() {
  const empresaId = getEmpresaId();
  const { toast, ToastContainer } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);  // solicitud abierta
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [accion, setAccion] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('pendiente');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/agencias-pendientes?empresa_id=${empresaId}`, { headers: getAuthHeaders() });
      const data = await r.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Error cargando solicitudes');
    }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  const filtered = filtroEstado ? items.filter(i => i.estado === filtroEstado) : items;
  const counts = items.reduce((acc, i) => { acc[i.estado] = (acc[i.estado] || 0) + 1; return acc; }, {});

  const aprobar = async () => {
    if (!detalle) return;
    setAccion(true);
    try {
      let userId = null;
      try { const u = JSON.parse(localStorage.getItem('user') || '{}'); userId = u.id || u.user_id || null; } catch {}
      const r = await fetch(`${API_BASE}/turismo/agencias-pendientes/${detalle.id}/aprobar`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ aprobada_por_user_id: userId }),
      });
      if (!r.ok) { const e = await r.json(); toast.error(e.detail || 'Error'); setAccion(false); return; }
      toast.success('Cambios aplicados a la agencia');
      setDetalle(null); cargar();
    } catch (e) { toast.error('Error de red'); }
    setAccion(false);
  };

  const rechazar = async () => {
    if (!detalle) return;
    if (!motivoRechazo.trim()) { toast.error('Indica el motivo del rechazo'); return; }
    setAccion(true);
    try {
      let userId = null;
      try { const u = JSON.parse(localStorage.getItem('user') || '{}'); userId = u.id || u.user_id || null; } catch {}
      const r = await fetch(`${API_BASE}/turismo/agencias-pendientes/${detalle.id}/rechazar`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ motivo: motivoRechazo, rechazada_por_user_id: userId }),
      });
      if (!r.ok) { const e = await r.json(); toast.error(e.detail || 'Error'); setAccion(false); return; }
      toast.success('Solicitud rechazada');
      setDetalle(null); setMotivoRechazo(''); cargar();
    } catch (e) { toast.error('Error de red'); }
    setAccion(false);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Solicitudes de actualización</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Cambios enviados por las agencias desde el portal público. Revisa y aprueba o rechaza.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={cargar} sx={{ color: '#0d9488' }}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Filtros chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {[
          { v: 'pendiente',         label: 'Pendientes' },
          { v: 'aprobada',          label: 'Aprobadas' },
          { v: 'rechazada',         label: 'Rechazadas' },
          { v: '',                  label: 'Todas' },
        ].map(f => {
          const n = f.v ? (counts[f.v] || 0) : items.length;
          const active = filtroEstado === f.v;
          const cfg = ESTADO_CFG[f.v];
          return (
            <Chip key={f.v} label={`${f.label}${n > 0 ? ` (${n})` : ''}`}
              onClick={() => setFiltroEstado(f.v)}
              sx={{
                fontWeight: 700, fontSize: 12, height: 28,
                bgcolor: active ? (cfg?.color || '#0d9488') : '#f1f5f9',
                color: active ? '#fff' : '#475569',
                cursor: 'pointer',
                '&:hover': { bgcolor: active ? (cfg?.color || '#0d9488') : '#e2e8f0' },
              }} />
          );
        })}
      </Box>

      {loading && (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>
      )}

      {!loading && filtered.length === 0 && (
        <Paper sx={{ p: 5, borderRadius: 3, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)' }}>
          <EmptyState icon={<HourglassEmptyIcon sx={{ fontSize: 56, color: '#cbd5e1' }} />}
            title={filtroEstado === 'pendiente' ? 'Sin solicitudes pendientes' : 'Sin resultados'}
            message={filtroEstado === 'pendiente' ? 'Cuando una agencia actualice sus datos desde el portal, aparecerá aquí.' : 'Cambia el filtro para ver otras solicitudes.'} />
        </Paper>
      )}

      <Stack spacing={1}>
        {filtered.map(s => {
          const cfg = ESTADO_CFG[s.estado] || ESTADO_CFG.pendiente;
          const resumen = s.cambios_resumen || {};
          const totalCambios = (resumen.campos?.length || 0)
            + (resumen.contactos_agregados?.length || 0)
            + (resumen.contactos_eliminados?.length || 0)
            + (resumen.contactos_modificados?.length || 0);
          return (
            <Paper key={s.id} onClick={() => setDetalle(s)}
              sx={{
                p: 1.5, borderRadius: 2, cursor: 'pointer',
                borderLeft: `4px solid ${cfg.color}`,
                '&:hover': { bgcolor: '#f8fafc' },
                display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
              }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2, bgcolor: '#fdf2f8',
                color: '#be185d', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <BusinessIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                  {s.agencia_nombre}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  Recibido: {s.created_at ? new Date(s.created_at).toLocaleString('es-CO') : '—'}
                  {totalCambios > 0 && ` · ${totalCambios} cambio(s)`}
                </Typography>
              </Box>
              <Chip size="small" label={cfg.label}
                sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 11 }} />
            </Paper>
          );
        })}
      </Stack>

      {/* SlideOver detalle */}
      {detalle && (
        <SlideOver open onClose={() => { setDetalle(null); setMotivoRechazo(''); }} variant="wide"
          title={detalle.agencia_nombre}
          subtitle={`Solicitud #${detalle.id} · ${ESTADO_CFG[detalle.estado]?.label || detalle.estado}`}
          icon={<EditIcon />}
          footer={
            detalle.estado === 'pendiente' ? (
              <>
                <Button onClick={() => setDetalle(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
                <Button startIcon={<CancelIcon />} color="error" disabled={accion}
                  onClick={rechazar}
                  sx={{ textTransform: 'none' }}>Rechazar</Button>
                <Button variant="contained" startIcon={<CheckCircleIcon />} disableElevation
                  disabled={accion} onClick={aprobar}
                  sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}>
                  Aprobar y aplicar
                </Button>
              </>
            ) : (
              <Button onClick={() => setDetalle(null)} sx={{ textTransform: 'none' }}>Cerrar</Button>
            )
          }>
          <DetalleSolicitud detalle={detalle}
            motivoRechazo={motivoRechazo} setMotivoRechazo={setMotivoRechazo} />
        </SlideOver>
      )}

      <ToastContainer />
    </Box>
  );
}


function DetalleSolicitud({ detalle, motivoRechazo, setMotivoRechazo }) {
  const resumen = detalle.cambios_resumen || {};
  const sinCambios = !resumen.campos?.length
    && !resumen.contactos_agregados?.length
    && !resumen.contactos_eliminados?.length
    && !resumen.contactos_modificados?.length;

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ fontSize: 12 }}>
        {detalle.estado === 'pendiente'
          ? 'Revisa los cambios. Al aprobar, se aplicarán inmediatamente a la agencia.'
          : detalle.estado === 'rechazada'
            ? `Rechazada${detalle.motivo_rechazo ? `: ${detalle.motivo_rechazo}` : ''}`
            : 'Cambios aplicados.'}
      </Alert>

      {sinCambios && (
        <Alert severity="warning" sx={{ fontSize: 12 }}>No se detectaron cambios en esta solicitud.</Alert>
      )}

      {/* Campos modificados */}
      {resumen.campos?.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0d9488', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            Datos modificados ({resumen.campos.length})
          </Typography>
          <Stack spacing={0.75}>
            {resumen.campos.map((c, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mb: 0.5 }}>
                  {CAMPOS_LABEL[c.campo] || c.campo}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ flex: 1, fontSize: 12, color: '#dc2626', textDecoration: 'line-through' }}>
                    {c.antes || '—'}
                  </Typography>
                  <ArrowForwardIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                  <Typography sx={{ flex: 1, fontSize: 12, color: '#16a34a', fontWeight: 700 }}>
                    {c.despues || '—'}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Contactos agregados */}
      {resumen.contactos_agregados?.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            <PersonAddIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.5 }} />
            Vendedores agregados ({resumen.contactos_agregados.length})
          </Typography>
          <Stack spacing={0.75}>
            {resumen.contactos_agregados.map((c, i) => (
              <Paper key={i} sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{c.nombre}</Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  {c.cargo && `${c.cargo} · `}{c.numero_whatsapp}
                  {c.correo && ` · ${c.correo}`}
                  {c.es_principal && ' · ⭐ Principal'}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Contactos modificados */}
      {resumen.contactos_modificados?.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0284c7', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            <EditIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.5 }} />
            Vendedores modificados ({resumen.contactos_modificados.length})
          </Typography>
          <Stack spacing={0.75}>
            {resumen.contactos_modificados.map((c, i) => (
              <Paper key={i} sx={{ p: 1.25, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{c.nombre}</Typography>
                {Object.entries(c.cambios).map(([k, v], j) => (
                  <Box key={j} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.25 }}>
                    <Typography sx={{ fontSize: 10, color: '#64748b', minWidth: 80, fontWeight: 600 }}>
                      {CAMPO_CONTACTO_LABEL[k] || k}:
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#dc2626', textDecoration: 'line-through' }}>
                      {String(v.antes ?? '—')}
                    </Typography>
                    <ArrowForwardIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                    <Typography sx={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>
                      {String(v.despues ?? '—')}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Contactos eliminados */}
      {resumen.contactos_eliminados?.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            <PersonRemoveIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.5 }} />
            Vendedores eliminados ({resumen.contactos_eliminados.length})
          </Typography>
          <Stack spacing={0.75}>
            {resumen.contactos_eliminados.map((c, i) => (
              <Paper key={i} sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#991b1b' }}>{c.nombre}</Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  {c.cargo && `${c.cargo} · `}{c.numero_whatsapp}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Motivo rechazo (solo si está pendiente) */}
      {detalle.estado === 'pendiente' && (
        <Box>
          <Divider sx={{ my: 1 }} />
          <TextField fullWidth multiline minRows={2} size="small"
            label="Motivo del rechazo (solo si vas a rechazar)"
            value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)}
            placeholder="Ej: NIT incorrecto. Por favor verifica y vuelve a enviar." />
        </Box>
      )}
    </Stack>
  );
}
